import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import toast from "react-hot-toast";
import { Eye, EyeOff, UserPlus, Calculator, CheckCircle, XCircle, Users } from "lucide-react";

// دالة التحقق من قوة كلمة المرور
const validatePassword = (password) => {
  const checks = {
    length: password.length >= 8,
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    uppercase: /[A-Z]/.test(password) || /[\u0600-\u06FF]/.test(password), // حروف كبيرة أو عربية
  };
  const passed = Object.values(checks).filter(Boolean).length;
  return { checks, strength: passed, isValid: passed >= 3 };
};

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    shop_name: "",
    referred_by: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ checks: {}, strength: 0, isValid: false });
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // تحديث قوة كلمة المرور
    if (name === "password") {
      setPasswordStrength(validatePassword(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // التحقق من قوة كلمة المرور
    if (!passwordStrength.isValid) {
      toast.error("كلمة المرور ضعيفة. يجب أن تحتوي على 8 أحرف على الأقل مع رقم ورمز خاص");
      return;
    }
    
    setLoading(true);
    try {
      await register(formData);
      toast.success("تم إنشاء الحساب بنجاح");
      navigate("/pending-activation");
    } catch (error) {
      toast.error(error.response?.data?.detail || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      dir="rtl"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.95), rgba(15, 23, 42, 0.98)), url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Card className="w-full max-w-md bg-slate-800/80 backdrop-blur-lg border-slate-700 animate-fade-in-up">
        <CardHeader className="text-center pb-2">
          <div className="flex flex-col items-center gap-4 mb-4">
            {/* Logo - Calculator Icon */}
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
              <Calculator className="w-10 h-10 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl font-heading font-bold text-white">
                إنشاء حساب جديد
              </CardTitle>
              <p className="text-slate-400 text-sm mt-1">
                Nazario-Hesabaty - نظام إدارة المتاجر
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-200">
                الاسم الكامل
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-200">
                البريد الإلكتروني
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-200">
                كلمة المرور
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500 pe-10"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              
              {/* مؤشر قوة كلمة المرور */}
              {formData.password && (
                <div className="mt-2 space-y-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          passwordStrength.strength >= level
                            ? passwordStrength.strength <= 2
                              ? "bg-red-500"
                              : passwordStrength.strength === 3
                              ? "bg-yellow-500"
                              : "bg-emerald-500"
                            : "bg-slate-700"
                        }`}
                      />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div className={`flex items-center gap-1 ${passwordStrength.checks.length ? "text-emerald-400" : "text-slate-500"}`}>
                      {passwordStrength.checks.length ? <CheckCircle size={12} /> : <XCircle size={12} />}
                      <span>8 أحرف على الأقل</span>
                    </div>
                    <div className={`flex items-center gap-1 ${passwordStrength.checks.number ? "text-emerald-400" : "text-slate-500"}`}>
                      {passwordStrength.checks.number ? <CheckCircle size={12} /> : <XCircle size={12} />}
                      <span>يحتوي رقم</span>
                    </div>
                    <div className={`flex items-center gap-1 ${passwordStrength.checks.special ? "text-emerald-400" : "text-slate-500"}`}>
                      {passwordStrength.checks.special ? <CheckCircle size={12} /> : <XCircle size={12} />}
                      <span>رمز خاص (!@#$)</span>
                    </div>
                    <div className={`flex items-center gap-1 ${passwordStrength.checks.uppercase ? "text-emerald-400" : "text-slate-500"}`}>
                      {passwordStrength.checks.uppercase ? <CheckCircle size={12} /> : <XCircle size={12} />}
                      <span>حرف كبير</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-200">
                رقم الهاتف
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                required
                data-testid="register-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="shop_name" className="text-slate-200">
                اسم المتجر
              </Label>
              <Input
                id="shop_name"
                name="shop_name"
                value={formData.shop_name}
                onChange={handleChange}
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                required
                data-testid="register-shop-input"
              />
            </div>

            {/* حقل "من أين سمعت عنا؟" (اختياري) */}
            <div className="space-y-2">
              <Label htmlFor="referred_by" className="text-slate-200 flex items-center gap-2">
                <Users size={16} className="text-amber-400" />
                من أين سمعت عنا؟ (اختياري)
              </Label>
              <Input
                id="referred_by"
                name="referred_by"
                value={formData.referred_by}
                onChange={handleChange}
                placeholder="مثال: أحمد، فيسبوك، صديق..."
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:border-emerald-500"
                data-testid="register-referred-by-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 btn-lift"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  جاري التسجيل...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus size={18} />
                  إنشاء حساب
                </span>
              )}
            </Button>

            <p className="text-center text-slate-400 text-sm">
              لديك حساب بالفعل؟{" "}
              <Link
                to="/login"
                className="text-emerald-500 hover:text-emerald-400 font-medium"
                data-testid="login-link"
              >
                تسجيل الدخول
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
